// Helpers
import { _get_vc_condition_handler } from './virtual_device_conditions';
import {
	getTemplateComponents,
	getTemplateService,
	translateTemplateTitle,
} from 'src/helpers/xt1_helpers/template_helpers';
import ActionsOnVirtualDevices from '../../SceneWizard/Process/Do/АddEditActionWizard/Steps/DeviceAction/Components/VirtualDevices/ActionsOnVirtualDevices.svelte';
import { getVCActionHandler } from './virtual_device_actions';
import { getVCIcon, VC_BUTTON_TRIGGER_EVENTS } from './virtual_component_utils';
import {
	format_gen2_addons_for_scenes,
	getAddonStatus,
	hasAddon,
} from 'src/helpers/addons/addon_helpers';
import { _ADD_ONS } from '../init_device_conditions';
// Types
import type { Device } from 'src/types/Device';
import type { TemplateComponent, TemplateService } from 'src/types/VirtualComponents';
import type { SceneActionComponentUI, SceneVirtualActionUIProps } from 'src/types/SceneActionUI';
import type { DeviceAction, DeviceActionUI } from 'src/types/DeviceActionUI';

/**
 * Initializes conditions for XT1 devices.
 *
 * @param {Device} device - The XT1 device to initialize conditions for.
 * @returns {Array} - List of condition objects for the device.
 */
export const init_xt1_device_conditions = (device: Device) => {
	const xt1_conditions = [];
	const templateService: TemplateService = getTemplateService(device);
	const conditions = templateService.ui?.conditions;
	if (!conditions) return xt1_conditions;

	const conditionsRoles = Object.keys(conditions);
	templateService.components.forEach((component) => {
		const role = component.status?._attrs?.role;
		if (conditionsRoles.includes(role)) {
			const componentType = component.key.split(':')[0];
			const handler = _get_vc_condition_handler(componentType);
			if (!handler) return;
			const condition = handler(componentType, component.config.id, component.config);
			translate_xt1_conditions(condition);
			xt1_conditions.push(condition);
		}
	});
	return [...xt1_conditions, ...addon_xt1_conditions(device)];
};

/**
 * Initializes available actions for XT1 devices by processing the virtual components
 * defined in the default template service.
 *
 * @param {Device} device - The XT1 device to initialize actions for.
 * @returns {SceneActionComponentUI[]} - List of scene action components.
 */
export const init_xt1_device_actions = (device: Device): SceneActionComponentUI[] => {
	const templateService: TemplateService = getTemplateService(device);
	const actions = templateService.ui?.actions;
	if (!actions) return [];

	const vc_actions: SceneVirtualActionUIProps[] = [];
	const actionsRoles = actions.map((action) => action.component);
	templateService.components.forEach((component) => {
		const role = component.status?._attrs?.role;
		if (actionsRoles.includes(role)) {
			const componentType = component.key.split(':')[0];
			const handler = getVCActionHandler(componentType);
			if (!handler) return;
			const action = handler(componentType, component.config.id, component.config);
			/**
			 * Virtual buttons for XT1 devices are used for main device control (e.g., opening/closing a valve).
			 * These actions are not intended to be triggered via scenes, so we only allow specific supported events.
			 * Related to #2783.
			 */
			if (action?.type === 'button' && action?.values?.length) {
				const event = actions.find((i) => i.component === role)?.params?.event;
				const supportedEvents = action.values.filter(
					(i) => i.value === VC_BUTTON_TRIGGER_EVENTS[event],
				);
				if (supportedEvents.length > 0) action.values = supportedEvents;
				action.shouldHideVirtualButtonEvents = action.values.length === 1;
			}
			translate_xt1_actions(action);
			vc_actions.push(action);
		}
	});
	return [
		{
			component: ActionsOnVirtualDevices as any,
			actions: vc_actions,
		},
	];
};

/**
 * Converts an XT1 action into a label list with an icon and condition_data title.
 *
 * @param {Device['id']} deviceID - The device ID.
 * @param {DeviceActionUI} action_data - The action object containing name, value, and optional index.
 * @returns {Object} - Formatted label with an icon, title, and color indicator.
 */
export const xt1_action_to_label_list = (action_data: DeviceActionUI) => {
	const action: DeviceAction = action_data.actions?.[0];
	const component: TemplateComponent = getTemplateComponents(
		action_data.device as Device,
		new RegExp(`${action?.name}:${action?.index}`),
	)?.[0];
	const name = component?.config?.name || '';
	const unit = component?.config?.meta?.ui?.unit || '';
	const icon = getVCIcon(component?.config) || 'fa fa-toggle-on';
	const actionValue = component?.config?.meta?.ui?.titles?.[action?.value] || action?.value;

	let value = name ? translateTemplateTitle(name) : '';
	if (action?.name !== 'button' && actionValue) {
		value += `: ${translateTemplateTitle(actionValue)} ${unit}`;
	}

	const isTrue = action?.value === 'true';
	const isFalse = action?.value === 'false';
	const hasColor = isTrue || isFalse;

	// Only use duotone for fa-toggle-on when it has color
	let finalIcon = icon;
	if (icon === 'fa fa-toggle-on' && hasColor && isTrue) {
		finalIcon = 'fa-duotone fa-light fa-toggle-on';
	}

	return {
		name: action?.name,
		icon: finalIcon,
		...(isTrue &&
			icon === 'fa fa-toggle-on' && {
				iconStyle:
					'--fa-primary-color: var(--color-success); --fa-secondary-color: var(--color-success-10); --fa-secondary-opacity: 1',
			}),
		...(isFalse && {
			iconColor: '--color-danger',
		}),
		value,
	};
};

/**
 * Translates the titles and key values of a condition object.
 */
const translate_xt1_conditions = (condition) => {
	condition.title = translateTemplateTitle(condition.title);
	condition.props.i18tr = translateTemplateTitle(condition.props?.i18tr);
	if (Array.isArray(condition.props?.key_values)) {
		condition.props.key_values = condition.props.key_values.map((kv) => ({
			...kv,
			value: translateTemplateTitle(kv.value),
		}));
	}
};

/**
 * Translates the name and values of a action object.
 */
const translate_xt1_actions = (action) => {
	action.name = translateTemplateTitle(action.name);
	if (Array.isArray(action?.values)) {
		action.values = action.values.map((value) => ({
			...value,
			title: translateTemplateTitle(value.title),
		}));
	}
};

/**
 * Generates XT1 conditions based on device add-ons.
 *
 * @param {Device} device - The XT1 device.
 * @returns {Array} - An array of XT1 conditions related to the device's add-ons.
 */
const addon_xt1_conditions = (device: Device) => {
	if (hasAddon(device)) {
		const addOns = format_gen2_addons_for_scenes(getAddonStatus(device));
		if (addOns && Object.keys(addOns).some((key) => addOns[key].length > 0)) {
			return _ADD_ONS(addOns);
		}
	}
	return [];
};
